import React, { useState } from "react";
import { useWaitlist } from "@/hooks/useWaitlist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Loader2 } from "lucide-react";

interface WaitlistFormProps {
  source: string;
  /** Placeholder for the email input */
  placeholder?: string;
  /** Text shown on the submit button */
  buttonText?: string;
  /** Visual variant: "hero" renders translucent inputs for dark backgrounds */
  variant?: "default" | "hero";
  /** Optional className for the outer wrapper */
  className?: string;
  /** Show the "How it Works" button (hero only) */
  onHowItWorks?: () => void;
}

const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const WaitlistForm: React.FC<WaitlistFormProps> = ({
  source,
  placeholder = "Enter your email…",
  buttonText = "Get Early Access",
  variant = "default",
  className = "",
  onHowItWorks,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signup } = useWaitlist();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!emailOk(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    const res = await signup(email, {
      source,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
    });
    setLoading(false);

    if (res.ok) {
      setSubmitted(true);
    } else {
      setError(
        "Something went wrong — please try again or contact us at hello@flexaura.me"
      );
    }
  };

  // ── Success state ──
  if (submitted) {
    return (
      <div
        className={`flex flex-col items-center sm:items-start gap-2 animate-in fade-in duration-300 ${className}`}
      >
        <CheckCircle
          className="w-10 h-10"
          style={{ color: "#0B7B6B" }}
          strokeWidth={2}
        />
        <p
          className={`text-lg font-semibold ${
            variant === "hero" ? "text-white" : "text-foreground"
          }`}
        >
          You're on the list!
        </p>
        <p
          className={`text-sm ${
            variant === "hero" ? "text-white/70" : "text-muted-foreground"
          }`}
        >
          Check your inbox — we've sent you a confirmation.
        </p>
      </div>
    );
  }

  // ── Hero variant (translucent inputs on dark bg) ──
  if (variant === "hero") {
    return (
      <div className={className}>
        <form
          onSubmit={handleSubmit}
          className="flex items-center justify-start gap-3 flex-col sm:flex-row"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            placeholder={placeholder}
            className="w-full sm:w-80 h-12 rounded-lg bg-white/10 backdrop-blur-sm placeholder-white/50 text-white px-4 outline-none border border-white/15 focus:border-primary/60 focus:bg-white/15 transition-all duration-300"
            aria-label="Email address"
            required
          />
          <button
            className="btn-primary h-12 px-6 rounded-lg text-white font-medium disabled:opacity-60 flex items-center gap-2"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Joining…
              </>
            ) : (
              buttonText
            )}
          </button>
          {onHowItWorks && (
            <button
              className="h-11 px-5 rounded-lg bg-transparent hover:bg-white/5 text-white/70 hover:text-white/90 border border-white/15 hover:border-white/25 transition-all duration-300"
              type="button"
              onClick={onHowItWorks}
            >
              How it Works
            </button>
          )}
        </form>
        {error && (
          <p className="mt-2 text-sm" style={{ color: "#DC2626" }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  // ── Default variant ──
  return (
    <div className={className}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-4"
      >
        <Input
          type="email"
          placeholder={placeholder}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          className="flex-1 h-12 text-base"
          required
        />
        <Button
          type="submit"
          size="lg"
          disabled={loading}
          className="whitespace-nowrap flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Joining…
            </>
          ) : (
            buttonText
          )}
        </Button>
      </form>
      {error && (
        <p className="mt-2 text-sm" style={{ color: "#DC2626" }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default WaitlistForm;
